import {
  usePickupLocation,
  useCreatePickupLocation,
  useUpdatePickupLocation,
} from '@/api';
import { Button, Input, FormSelect, PhoneInput } from '@/components/atoms';
import { FormField } from '@/components/molecules';
import { pickupLocationSchema } from '@/validation-schema';
import type { PickupLocationFormData } from '@/validation-schema';
import { yupResolver } from '@hookform/resolvers/yup';
import type { Resolver } from 'react-hook-form';
import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  COUNTRY_OPTIONS,
  getStateOptionsForCountry,
} from '@/lib/constants/countries-states';

interface PickupLocationFormProps {
  id?: number;
  mode: 'create' | 'update';
  onSuccess: () => void;
}

export const PickupLocationForm = ({
  id,
  mode,
  onSuccess,
}: PickupLocationFormProps) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
    setValue,
    watch,
    reset,
    trigger,
  } = useForm<PickupLocationFormData>({
    resolver: yupResolver(
      pickupLocationSchema
    ) as Resolver<PickupLocationFormData>,
    mode: 'onChange',
  });

  const { data: location } = usePickupLocation(
    mode === 'update' ? id : undefined
  );

  useEffect(() => {
    if (location && mode === 'update') {
      setValue('name', location.name);
      setValue('addressLine1', location.addressLine1);
      setValue('addressLine2', location.addressLine2 ?? '');
      setValue('city', location.city ?? '');
      setValue('state', location.state ?? '');
      setValue('country', location.country ?? '');
      const country = location.country ?? '';
      const state = location.state ?? '';
      const countryValues = COUNTRY_OPTIONS.map((o) => o.value);
      if (country && !countryValues.includes(country)) {
        setValue('country', 'Other');
        setValue('countryOther', country);
      } else {
        setValue('countryOther', '');
      }
      const stateOpts = getStateOptionsForCountry(country);
      const stateValues = stateOpts.map((o) => o.value);
      if (state && !stateValues.includes(state)) {
        setValue('state', 'Other');
        setValue('stateOther', state);
      } else {
        setValue('stateOther', '');
      }
      setValue('zipCode', location.zipCode ?? '');
      setValue('phoneNumber', location.phoneNumber ?? '');
      trigger();
    } else {
      reset();
    }
  }, [location, mode, setValue, reset, trigger]);

  const { mutate: create, isPending } = useCreatePickupLocation(onSuccess);
  const { mutate: update, isPending: isUpdatePending } =
    useUpdatePickupLocation(onSuccess);

  const country = watch('country');
  const state = watch('state');
  const stateOptions = getStateOptionsForCountry(country);

  const submit = (data: PickupLocationFormData) => {
    const countryValue =
      data.country === 'Other' && data.countryOther?.trim()
        ? data.countryOther.trim()
        : data.country;
    const stateValue =
      data.state === 'Other' && data.stateOther?.trim()
        ? data.stateOther.trim()
        : data.state;
    const payload = {
      ...data,
      addressLine2: data.addressLine2 || null,
      city: data.city || null,
      state: stateValue || null,
      country: countryValue || null,
      zipCode: data.zipCode || null,
      phoneNumber: data.phoneNumber || null,
    };
    if (mode === 'update' && id) {
      update({ id, ...payload });
    } else {
      create(payload);
    }
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="grid grid-cols-1 gap-3">
      <FormField label="Location name" error={errors.name?.message}>
        <Input placeholder="e.g. Main Branch" {...register('name')} />
      </FormField>
      <FormField label="Address line 1" error={errors.addressLine1?.message}>
        <Input
          placeholder="e.g. 12 Lagos Street"
          {...register('addressLine1')}
        />
      </FormField>
      <FormField
        label="Address line 2 (optional)"
        error={errors.addressLine2?.message}
      >
        <Input placeholder="Optional" {...register('addressLine2')} />
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="City" error={errors.city?.message}>
          <Input placeholder="e.g. Lagos" {...register('city')} />
        </FormField>
        <FormField label="Country" error={errors.country?.message}>
          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <FormSelect
                placeholder="Select country"
                value={field.value ?? ''}
                onChange={(v) => {
                  field.onChange(v);
                  setValue('state', '');
                  if (v !== 'Other') setValue('countryOther', '');
                }}
                options={COUNTRY_OPTIONS}
              />
            )}
          />
        </FormField>
        {country === 'Other' && (
          <div className="col-span-2">
            <FormField
              label="Specify country"
              error={errors.countryOther?.message}
            >
              <Input
                placeholder="e.g. Cameroon"
                {...register('countryOther')}
              />
            </FormField>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="State" error={errors.state?.message}>
          <Controller
            name="state"
            control={control}
            render={({ field }) => (
              <FormSelect
                placeholder="Select state"
                value={field.value ?? ''}
                onChange={(v) => {
                  field.onChange(v);
                  if (v !== 'Other') setValue('stateOther', '');
                }}
                options={stateOptions.length > 0 ? stateOptions : []}
              />
            )}
          />
        </FormField>
        {state === 'Other' && (
          <div className="col-span-2">
            <FormField label="Specify state" error={errors.stateOther?.message}>
              <Input
                placeholder="e.g. Central Region"
                {...register('stateOther')}
              />
            </FormField>
          </div>
        )}
        <FormField label="Zip code (optional)" error={errors.zipCode?.message}>
          <Input placeholder="e.g. 100001" {...register('zipCode')} />
        </FormField>
      </div>
      <FormField label="Phone number" error={errors.phoneNumber?.message}>
        <Controller
          name="phoneNumber"
          control={control}
          render={({ field }) => (
            <PhoneInput
              country="ng"
              value={field.value ?? ''}
              onChange={(value) => field.onChange(value)}
              inputProps={{ 'aria-invalid': !!errors.phoneNumber }}
            />
          )}
        />
      </FormField>
      <Button
        className="col-span-full"
        label={`${mode === 'create' ? 'Create' : 'Update'} pickup location`}
        size="lg"
        type="submit"
        isLoading={isPending || isUpdatePending}
        disabled={!isValid}
      />
    </form>
  );
};
